import React from "react";
import styles from "./Home.module.css";
import Panel from "./Panel";
import DeadlineRow from "./DeadlineRow";
import { bookingLines } from "./homeData";
import { saveTimesheetCell } from "../../../services/timesheetService";
import { dayName, mondayOf, toISODate, weekDates } from "../../../utils/weekDates";

const round = (n) => Math.round(n * 10) / 10;
const sum = (rows, pick) => rows.reduce((s, r) => s + (Number(pick(r)) || 0), 0);
// Ids arrive as strings (bigint) on bookings and numbers on timesheets.
const sameLine = (a, b) => String(a.project_id ?? "") === String(b.project_id ?? "") && String(a.activity_type_id ?? "") === String(b.activity_type_id ?? "");
const hhmm = (v) => new Date(v).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// The signed-in person's week: bookings per day, log time from today's bookings, booked vs logged, leave and deadlines.
export default function MyWork({ data, weekStart, canLog, runBatch, reload, onRequestLeave, onOpenProject }) {
  const today = toISODate(new Date());
  const days = weekDates(weekStart).slice(0, 5);
  const bookings = data.weekSchedule;
  const entries = data.thisWeekTimesheets;
  const { workload, leaveCounts, leaveByType } = data;

  const loggedOn = (date, line) => sum(entries.filter((e) => e.work_date === date && sameLine(e, line)), (e) => e.hours_worked);
  const todayLines = bookingLines(bookings, today);
  const open = todayLines.filter((l) => loggedOn(today, l) < l.hours);

  const setLines = (lines, hoursFor) => Promise.all(lines.map((l) => saveTimesheetCell({
    employee_id: data.employee_id, project_id: l.project_id, activity_type_id: l.activity_type_id, work_date: today, hours: hoursFor(l), mode: "set",
  })));
  const log = (lines) => {
    const before = new Map(lines.map((l) => [l.key, loggedOn(today, l)]));
    return runBatch(() => setLines(lines, (l) => l.hours), reload, () => `Logged ${round(sum(lines, (l) => l.hours))}h from today's bookings.`, () => setLines(lines, (l) => before.get(l.key)));
  };

  return (
    <>
      <Panel title="My week" tools={<span className={styles.chip}>{round(workload.booked_hours)}h booked of {round(workload.weekly_hours)}h</span>}>
        <div className={styles.week}>
          {days.map((d) => {
            const rows = bookings.filter((b) => b.work_date === d);
            return (
              <div key={d} className={`${styles.day} ${d === today ? styles.dayToday : ""}`}>
                <div className={styles.dayHead}>{dayName(d)} {Number(d.slice(8))}</div>
                {rows.map((b) => (
                  <div key={`${b.id}-${d}`} className={styles.event}>
                    <b><span>{b.project_name || b.milestone_name || b.booking_type}</span><span>{round(b.hours_allocated)}h</span></b>
                    <small>{[b.client_company, b.is_time_specific && `${hhmm(b.start_datetime)}–${hhmm(b.end_datetime)}`].filter(Boolean).join(" · ")}</small>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </Panel>

      <div className={styles.twoCol}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          {weekStart === mondayOf(today) && (
            <Panel
              title="Today"
              count={todayLines.length || null}
              countTone="neutral"
              tools={canLog && open.length > 1 && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => log(open)}>Log all · {round(sum(open, (l) => l.hours))}h</button>}
            >
              {todayLines.length === 0 ? <div className={styles.empty}>Nothing booked today.</div> : todayLines.map((l) => (
                <div key={l.key} className={styles.todayRow}>
                  <div>
                    <div className={styles.dlTitle}>{[l.client, l.title].filter(Boolean).join(" · ")}</div>
                    <div className={styles.dlSub}>{round(l.hours)}h booked</div>
                  </div>
                  {loggedOn(today, l) >= l.hours
                    ? <span className={styles.logged_ok}>Logged ✓</span>
                    : canLog && <button type="button" className={styles.btn} onClick={() => log([l])}>Log {round(l.hours)}h</button>}
                </div>
              ))}
            </Panel>
          )}

          <Panel title="This week" tools={<span className={styles.chip}>{round(sum(entries, (e) => e.hours_worked))}h logged · {round(workload.booked_hours)}h booked · {round(workload.weekly_hours)}h capacity</span>}>
            <div className={styles.bars}>
              {days.map((d) => {
                const booked = sum(bookings.filter((b) => b.work_date === d), (b) => b.hours_allocated);
                const logged = sum(entries.filter((e) => e.work_date === d), (e) => e.hours_worked);
                const scale = 90 / Math.max(8, booked, logged);
                return (
                  <div key={d} className={styles.barCol}>
                    <div className={styles.barPair}>
                      <div className={styles.bar} style={{ height: booked * scale }} title={`Booked ${round(booked)}h`} />
                      <div className={`${styles.bar} ${styles.barLogged}`} style={{ height: logged * scale }} title={`Logged ${round(logged)}h`} />
                    </div>
                    <span className={styles.barLabel}>{dayName(d).toUpperCase()}</span>
                    <span className={styles.barNums}>{round(logged)} / {round(booked)}h</span>
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <Panel title="Leave balance" tools={onRequestLeave && <button type="button" className={styles.btn} onClick={onRequestLeave}>Request leave</button>}>
            <span className={styles.bigNum}>{round(leaveCounts.remaining_days)}</span> <span className={styles.tileDelta}>days left · {round(leaveCounts.used_days)} used · {round(leaveCounts.pending_days)} pending</span>
            {leaveByType.map((t) => (
              <div key={t.leave_type_name} className={styles.leaveRow}>
                <div><span>{t.leave_type_name}</span><b>{round(t.remaining_days)} of {round(t.total_available_days)} left</b></div>
                <div className={styles.track}><div className={styles.trackFill} style={{ width: `${Number(t.total_available_days) ? (t.remaining_days / t.total_available_days) * 100 : 0}%` }} /></div>
              </div>
            ))}
          </Panel>

          <Panel title="My deadlines">
            {data.upcomingDeadlines.length === 0 ? <div className={styles.empty}>No upcoming deadlines.</div> : data.upcomingDeadlines.map((d) => (
              <DeadlineRow
                key={`${d.milestone_id}-${d.project_name}`}
                date={String(d.due_date).slice(0, 10)}
                today={today}
                title={d.milestone_name}
                sub={[d.client_company, d.project_name].filter(Boolean).join(" · ")}
                onClick={d.project_id ? () => onOpenProject(d.project_id) : undefined}
              />
            ))}
          </Panel>
        </div>
      </div>
    </>
  );
}
